import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminCancellation } from '../../services/cancellationsApi';
import { useToast } from '../../components/ToastContext';
import { formatDateRange } from '../../utils/dateFormat';

const StatusChip = ({ status }) => (
  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
    {status}
  </span>
);

const RefundBreakdown = ({ snapshot, currency }) => {
  if (!snapshot) return null;
  const lines = snapshot.breakdown || [];
  return (
    <div className="space-y-1">
      {lines.map((line, idx) => (
        <div key={line.label || idx} className="flex justify-between text-sm text-slate-700">
          <span>{line.label}</span>
          <span>
            {currency} {line.amount}
          </span>
        </div>
      ))}
      <div className="flex justify-between font-semibold text-slate-900 pt-2 border-t border-slate-100">
        <span>Refundable</span>
        <span>
          {currency} {snapshot.refundable_amount || 0}
        </span>
      </div>
    </div>
  );
};

const AdminCancellationDetail = ({ token }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [note, setNote] = useState('');
  const [refundPayload, setRefundPayload] = useState({ provider: 'manual', reference: '', amount: '' });
  const [processedRef, setProcessedRef] = useState({ reference: '', processed_at: '' });
  const [failureReason, setFailureReason] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminCancellation.get(id, token);
      setDetail(res);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const doAction = async (fn, success) => {
    setActionLoading(true);
    try {
      await fn();
      await load();
      if (success) toast.success(success);
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !detail) {
    return <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">Loading...</div>;
  }
  if (error) {
    return (
      <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }
  if (!detail) return null;

  const { cancellation, booking, refunds, events } = detail;
  const refundSnapshot = cancellation?.refund_snapshot || {};
  const latestRefund = refunds?.[0];

  return (
    <div className="admin-card space-y-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Cancellation</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">{booking?.booking_code}</h1>
          <p className="text-sm text-slate-600">{booking?.property_title}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip status={cancellation?.status} />
          <button
            type="button"
            onClick={() => navigate('/admin/cancellations')}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Back
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Guest</p>
                <p className="text-sm font-semibold text-slate-900">{booking?.guest_name || 'Guest'}</p>
                <p className="text-xs text-slate-500">{booking?.guest_email || booking?.guest_phone}</p>
              </div>
              <div className="text-right text-sm text-slate-700">
                <p>{booking?.currency} {booking?.total_amount || booking?.totals_json?.total || 0}</p>
                <p className="text-xs text-slate-500 capitalize">{booking?.payment_status}</p>
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3 text-sm text-slate-700">
              <div>
                <p className="text-xs text-slate-500">Dates</p>
                  <p>{formatDateRange(booking?.check_in_date, booking?.check_out_date)}</p>
                </div>
              <div>
                <p className="text-xs text-slate-500">Host</p>
                <p>{booking?.host_name || 'Host'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Policy</p>
                <p>{cancellation?.policy_snapshot?.name || cancellation?.policy_snapshot?.code}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">Refund breakdown</p>
              <p className="text-xs text-slate-500">Requested at {new Date(cancellation.requested_at).toLocaleString()}</p>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                <p className="text-xs text-slate-500">Refund amount</p>
                <p className="text-lg font-bold text-emerald-700">
                  {cancellation?.currency} {cancellation?.refund_amount || 0}
                </p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                <p className="text-xs text-slate-500">Host payout adjustment</p>
                <p className="text-lg font-semibold text-slate-800">
                  {cancellation?.currency} {cancellation?.host_payout_adjustment || 0}
                </p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                <p className="text-xs text-slate-500">Platform fee refund</p>
                <p className="text-lg font-semibold text-slate-800">
                  {cancellation?.currency} {cancellation?.platform_fee_refund || 0}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <RefundBreakdown snapshot={refundSnapshot} currency={cancellation?.currency || booking?.currency} />
            </div>
            {cancellation?.reason && (
              <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
                <p className="font-semibold text-slate-800">Cancellation reason</p>
                <p>{cancellation.reason}</p>
              </div>
            )}
            {cancellation?.host_action && (
              <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
                <p className="font-semibold text-slate-800">Host response</p>
                <p className="capitalize">{cancellation.host_action}</p>
                {cancellation.host_action_note && <p className="text-xs text-slate-500">{cancellation.host_action_note}</p>}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-900">Timeline</p>
            <div className="mt-3 space-y-3">
              {(events || []).map((evt) => (
                <div key={evt.id} className="flex gap-3 text-sm">
                  <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
                  <div>
                    <p className="font-semibold text-slate-800 capitalize">
                      {evt.event_type.replace(/_/g, ' ')} {evt.to_status ? `(${evt.to_status})` : ''}
                    </p>
                    <p className="text-xs text-slate-500">{new Date(evt.created_at).toLocaleString()}</p>
                    {evt.metadata?.note && <p className="text-xs text-slate-600">{evt.metadata.note}</p>}
                  </div>
                </div>
              ))}
              {!events?.length && <p className="text-sm text-slate-500">No events yet.</p>}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-900">Decision</p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add admin note"
              className="mt-2 w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              rows={2}
            />
            <div className="mt-3 flex flex-col gap-2">
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => doAction(() => adminCancellation.approve(id, note, token), 'Approved')}
                className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                Approve cancellation
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => doAction(() => adminCancellation.reject(id, note, token), 'Rejected')}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-900">Refund</p>
            <div className="mt-2 grid gap-2">
              <input
                type="text"
                placeholder="Provider"
                value={refundPayload.provider}
                onChange={(e) => setRefundPayload((p) => ({ ...p, provider: e.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Reference"
                value={refundPayload.reference}
                onChange={(e) => setRefundPayload((p) => ({ ...p, reference: e.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              />
              <input
                type="number"
                placeholder="Amount"
                value={refundPayload.amount}
                onChange={(e) => setRefundPayload((p) => ({ ...p, amount: e.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              />
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => doAction(() => adminCancellation.initiateRefund(id, refundPayload, token), 'Refund initiated')}
                className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 disabled:opacity-50"
              >
                Initiate refund
              </button>
            </div>

            <div className="mt-4 border-t border-slate-100 pt-3">
              <p className="text-sm font-semibold text-slate-900">Mark refunded</p>
              <input
                type="text"
                placeholder="Reference"
                value={processedRef.reference}
                onChange={(e) => setProcessedRef((p) => ({ ...p, reference: e.target.value }))}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              />
              <input
                type="datetime-local"
                value={processedRef.processed_at}
                onChange={(e) => setProcessedRef((p) => ({ ...p, processed_at: e.target.value }))}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              />
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => doAction(() => adminCancellation.markRefunded(id, processedRef, token), 'Marked refunded')}
                className="mt-2 w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                Mark refunded
              </button>
            </div>

            <div className="mt-4 border-t border-slate-100 pt-3">
              <p className="text-sm font-semibold text-slate-900">Mark failed</p>
              <textarea
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                placeholder="Failure reason"
                className="mt-2 w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                rows={2}
              />
              <button
                type="button"
                disabled={actionLoading || !failureReason}
                onClick={() => doAction(() => adminCancellation.markRefundFailed(id, { reason: failureReason }, token), 'Marked failed')}
                className="mt-2 w-full rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                Mark refund failed
              </button>
            </div>

            {latestRefund && (
              <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
                <p className="font-semibold text-slate-800">Latest refund</p>
                <p>Status: {latestRefund.status}</p>
                {latestRefund.reference && <p>Reference: {latestRefund.reference}</p>}
                {latestRefund.processed_at && <p>Processed at: {new Date(latestRefund.processed_at).toLocaleString()}</p>}
                {latestRefund.failure_reason && <p className="text-red-600">Failure: {latestRefund.failure_reason}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCancellationDetail;
